import { detectScript } from '../../detectScript.js';
import { extractKnockoutTarget } from '../../utils/knockoutInput.js';
import { generateKnockout } from '../../services/apiClient.js';
import { geolocDeniedMessage } from '../../services/geolocationService.js';
import { applyAtPrefix } from '../../utils/stringUtils.js';

// Réexporté : déjà importé d'ici par une partie du dashboard.
export { applyAtPrefix };

export function createProposerFeature({
  input,
  pseudoInput,
  pseudoRow,
  generateBtn,
  preview,
  previewFooter,
  animPreviewFooter,
  status,
  loadFont,
  generateSVG,
  normalizeFilename,
  renderPunchlineAnimation,
  renderPunchlineAnimationFromData,
  getDashboardItems,
  resyncDeletions,
  geolocationService,
  addToDashboard,
  setLastOutput,
}) {
  const knockoutGenerateRow = document.getElementById('knockoutGenerateRow');
  const knockoutGenerateBtn = document.getElementById('knockoutGenerateBtn');
  const knockoutGenerateStatus = document.getElementById('knockoutGenerateStatus');

  let previewLockedH = null;
  let debounceTimer = null;
  let geolocPending = false;
  // Ecriture non-latine detectee + mot cible extrait, en attente (ou deja
  // pourvu) du resultat de "Generer l'apercu" -- null tant qu'aucune
  // ecriture non-latine valide n'est detectee dans le champ.
  let knockoutState = null;

  function clearGeolocUI() {
    const statusEl = document.getElementById('geolocStatus');
    const actionsEl = document.getElementById('geolocActions');
    if (statusEl) statusEl.textContent = '';
    if (actionsEl) actionsEl.hidden = true;
  }

  async function attemptGeolocation() {
    const statusEl = document.getElementById('geolocStatus');
    const actionsEl = document.getElementById('geolocActions');
    if (actionsEl) actionsEl.hidden = true;
    if (statusEl) statusEl.textContent = '⏳ Localisation…';
    try {
      const pos = await geolocationService.getCurrentPosition();
      if (statusEl) statusEl.textContent = '✓ Position enregistrée';
      setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 3000);
      return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch (geoErr) {
      console.warn('[geoloc] Échec de la géolocalisation:', geoErr?.message ?? geoErr);
      if (statusEl) statusEl.textContent = geoErr?.code === 1
        ? `⚠ ${geolocDeniedMessage()}`
        : '⚠ Position non disponible — désactive ton VPN si actif.';
      if (actionsEl) actionsEl.hidden = false;
      return null;
    }
  }

  async function finalizeSubmit(text, pseudo, lat, lng, knockout) {
    await addToDashboard(text, pseudo, lat, lng, knockout);
    showHint('hint');
  }

  function resolveGeolocationThenSubmit(text, pseudo, knockout) {
    return attemptGeolocation().then((pos) => {
      if (pos) return finalizeSubmit(text, pseudo, pos.lat, pos.lng, knockout);

      // Échec : on attend que l'utilisateur choisisse de réessayer ou de continuer sans position
      return new Promise((resolve, reject) => {
        const retryBtn = document.getElementById('geolocRetryBtn');
        const skipBtn  = document.getElementById('geolocSkipBtn');

        function cleanup() {
          retryBtn?.removeEventListener('click', onRetry);
          skipBtn?.removeEventListener('click', onSkip);
        }
        function onRetry() {
          cleanup();
          resolveGeolocationThenSubmit(text, pseudo, knockout).then(resolve, reject);
        }
        function onSkip() {
          cleanup();
          clearGeolocUI();
          finalizeSubmit(text, pseudo, null, null, knockout).then(resolve, reject);
        }

        retryBtn?.addEventListener('click', onRetry);
        skipBtn?.addEventListener('click', onSkip);
      });
    });
  }

  function showHint(id) {
    document.getElementById('hint').hidden = id !== 'hint';
    document.getElementById('hintDuplicate').hidden = id !== 'hintDuplicate';
    document.getElementById('hintDefault').hidden = id !== 'hintDefault';
  }

  async function renderAll() {
    const text = input.value.trim();
    renderPunchlineAnimation(text);

    if (!text) {
      preview.innerHTML = '<span class="empty">Le SVG apparaitra ici</span>';
      preview.style.width = '';
      return;
    }

    try {
      const buf = await loadFont();
      const svg = await generateSVG(text, buf, '#000000');
      preview.innerHTML = svg;
      setLastOutput(svg, normalizeFilename(text) + '.svg');

      const svgEl = preview.querySelector('svg');
      const svgW = svgEl ? parseFloat(svgEl.getAttribute('width')) : 0;
      const svgH = svgEl ? parseFloat(svgEl.getAttribute('height')) : 0;
      const parentW = preview.parentElement.clientWidth || Math.min(window.innerWidth - 40, 600);

      if (!previewLockedH) {
        previewLockedH = Math.ceil(svgH * 0.80) + 6;
        preview.style.height = previewLockedH + 'px';
        preview.classList.add('preview--height-locked');
      }

      const contentH = previewLockedH - 6;
      const scaledW = svgH > 0 ? svgW * contentH / svgH : svgW;
      preview.style.width = Math.min(Math.ceil(scaledW) + 26, parentW) + 'px';
    } catch (error) {
      status.textContent = `Erreur : ${error.message}`;
      console.error(error);
    }
  }

  // Efface toute taille figee sur .preview (width en px + height en px +
  // classe preview--height-locked, cf. renderAll ci-dessus) -- sans ca, le
  // verrouillage calcule pour une phrase LATINE (evite le jank a la frappe)
  // restait applique tel quel quand on passait a une ecriture non-latine
  // (onGenerateKnockoutClick n'y touchait jamais) : le SVG knockout heritait
  // d'une hauteur minuscule et sans rapport avec sa propre taille, au lieu
  // de remplir l'espace disponible en gardant son ratio (constate par
  // Rudy, 22 septembre 2026 -- apercu statique "trop petit").
  function resetPreviewBoxSize() {
    preview.style.width = '';
    preview.style.height = '';
    preview.classList.remove('preview--height-locked');
  }

  function resetPreviewPlaceholder(message) {
    preview.innerHTML = `<span class="empty">${message}</span>`;
    resetPreviewBoxSize();
    renderPunchlineAnimation('');
  }

  // Ecritures non-latines : pas d'apercu en direct a la frappe (le pipeline
  // serveur prend 15-90s) -- on met a jour seulement la legende + la
  // visibilite/l'etat du bouton "Generer l'apercu", jamais de generation
  // automatique.
  function updateScriptUI() {
    const text = input.value;
    const { script } = detectScript(text);

    if (script === 'Latn') {
      knockoutGenerateRow.hidden = true;
      knockoutState = null;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(renderAll, 120);
      return;
    }

    clearTimeout(debounceTimer);
    const hasText = text.trim().length > 0;
    // Marqueur *mot* optionnel : comme le latin sans MAJUSCULES, une phrase
    // sans mot entoure d'asterisques reste generable, juste sans rectangle
    // knockout (target: null jusqu'au pipeline serveur -- cf.
    // knockoutService.js/build-knockout.mjs).
    const extracted = hasText ? extractKnockoutTarget(text) : null;
    const sentence = extracted ? extracted.sentence : text.trim();
    const target = extracted ? extracted.target : null;
    knockoutGenerateRow.hidden = !hasText;
    knockoutGenerateBtn.disabled = !hasText;
    knockoutGenerateStatus.textContent = '';
    knockoutState = hasText
      ? { script, sentence, target, svg: null, lineData: null }
      : null;

    if (!hasText) {
      resetPreviewPlaceholder('Le SVG apparaitra ici');
    } else {
      resetPreviewPlaceholder('Clique sur « Générer l’aperçu »');
    }
  }

  async function onGenerateKnockoutClick() {
    if (!knockoutState) return;
    const { script, sentence, target } = knockoutState;
    knockoutGenerateBtn.disabled = true;
    knockoutGenerateStatus.textContent = '⏳ génération en cours (jusqu’à 90s selon l’écriture)…';
    resetPreviewBoxSize();
    preview.innerHTML = '<span class="empty">Génération en cours…</span>';
    // position > 0 : en attente qu'un slot se libere (cf. MAX_CONCURRENT
    // cote serveur, knockoutService.js) -- position 0 : le calcul a
    // reellement demarre, on revient au message "génération en cours".
    function onQueueUpdate(position) {
      knockoutGenerateStatus.textContent = position > 0
        ? `En file d'attente : ${position} devant toi.`
        : '⏳ génération en cours (jusqu’à 90s selon l’écriture)…';
    }
    try {
      const result = await generateKnockout({ script, sentence, target }, onQueueUpdate);
      // Le champ a pu changer pendant l'attente : on ignore un resultat perime.
      if (!knockoutState || knockoutState.sentence !== sentence || knockoutState.target !== target) return;
      knockoutState.svg = result.svg;
      knockoutState.lineData = result.lineData;
      resetPreviewBoxSize();
      preview.innerHTML = result.svg;
      setLastOutput(result.svg, normalizeFilename(sentence) + '.svg');
      renderPunchlineAnimationFromData(result.lineData);
      knockoutGenerateStatus.textContent = '';
      previewFooter.classList.add('visible');
      animPreviewFooter.classList.add('visible');
    } catch (error) {
      knockoutGenerateStatus.textContent = `Erreur : ${error.message}`;
      preview.innerHTML = '<span class="empty">Échec de la génération</span>';
      console.error(error);
    } finally {
      knockoutGenerateBtn.disabled = false;
    }
  }

  function resetToInitialState() {
    showHint('hintDefault');
    previewFooter.classList.remove('visible');
    animPreviewFooter.classList.remove('visible');
    document.querySelector('.pseudo-confirm')?.remove();
    pseudoRow.hidden = false;
    clearGeolocUI();
  }

  async function generate() {
    const { script } = detectScript(input.value);
    const isServerScript = script !== 'Latn';
    let text;
    let knockout = null;

    if (isServerScript) {
      if (!knockoutState || knockoutState.script !== script || !knockoutState.svg || !knockoutState.lineData) {
        status.textContent = 'Génère d’abord l’aperçu avec le bouton « Générer l’aperçu ».';
        return;
      }
      text = knockoutState.sentence;
      knockout = {
        script: knockoutState.script, svg: knockoutState.svg, lineData: knockoutState.lineData,
        target: knockoutState.target,
      };
    } else {
      text = input.value.trim();
      await renderAll();
    }

    const pseudo = pseudoInput.value.trim();
    previewFooter.classList.add('visible');
    animPreviewFooter.classList.add('visible');

    if (text) {
      const isLocalDuplicate = () => getDashboardItems().some((item) => item.text.trim().toLowerCase() === text.toLowerCase());
      // La liste locale peut contenir une phrase supprimee entre-temps
      // (onglet mobile reste en arriere-plan, SSE 'delete-punchline' perdu) :
      // on la resynchronise avec la base avant de conclure au doublon.
      if (isLocalDuplicate()) {
        try {
          await resyncDeletions();
        } catch (error) {
          console.error('Resync avant doublon:', error);
        }
        if (isLocalDuplicate()) {
          showHint('hintDuplicate');
          return;
        }
      }

      if (geolocPending) return;

      if (document.getElementById('geolocToggle')?.checked) {
        geolocPending = true;
        generateBtn.disabled = true;
        try {
          await resolveGeolocationThenSubmit(text, pseudo, knockout);
        } finally {
          geolocPending = false;
          generateBtn.disabled = false;
        }
      } else {
        clearGeolocUI();
        await finalizeSubmit(text, pseudo, null, null, knockout);
      }
    }

    pseudoRow.hidden = true;
    document.querySelector('.pseudo-confirm')?.remove();
    if (pseudo) {
      const confirm = document.createElement('div');
      confirm.className = 'pseudo-confirm';
      confirm.innerHTML =
        '<span class="pseudo-confirm-check"><svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1.5,5 4,8 8.5,2"/></svg></span>' +
        'Ton pseudo est enregistré !';
      pseudoRow.parentElement.insertBefore(confirm, pseudoRow.nextSibling);
    }
  }

  function bindEvents() {
    input.addEventListener('input', updateScriptUI);

    pseudoInput.addEventListener('input', () => applyAtPrefix(pseudoInput));
    generateBtn.addEventListener('click', generate);
    knockoutGenerateBtn.addEventListener('click', onGenerateKnockoutClick);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') generate();
    });
    input.addEventListener('focus', resetToInitialState);
  }

  return {
    showHint,
    renderAll,
    generate,
    bindEvents,
  };
}
